import React, {MouseEvent, ReactNode, useEffect, useRef, useState} from 'react';
import {Loader, Search} from "xps-react";

export interface PageableSearchResults<TResult> {
    hasMoreResults: boolean,
    results: TResult[]
}

type TypeaheadSearchBarProps<Type> = {
    performSearch: (searchTerm: string, page?: number, signal?: AbortSignal) => Promise<PageableSearchResults<Type>>,
    minSearchTermLength?: number,
    placeholder: string,
    onSelect?: (result: Type) => void,
    displayResult: (result: Type) => ReactNode,
    resultHeader?: ReactNode,
    searchType?: string
};

export default function TypeaheadSearchBar<Type>({
                                                     performSearch,
                                                     minSearchTermLength = 1,
                                                     placeholder,
                                                     onSelect,
                                                     displayResult,
                                                     resultHeader,
                                                     searchType = ""
                                                 }: TypeaheadSearchBarProps<Type>) {
    const [results, setResults] = useState<Type[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMoreResults, setHasMoreResults] = useState(false);
    const [searchResultsType, setSearchResultsType] = useState<string>(searchType);
    const [isSearching, setIsSearching] = useState(false);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const searchController = useRef<AbortController | null>(null);

    let componentUnmounted = useRef(false);
    useEffect(() => {
        return () => {
            componentUnmounted.current = true;
        }
    }, []);

    useEffect(() => {
        if (searchResultsType !== searchType) {
            clearResults();
            setSearchResultsType(searchType);
        }
    }, [searchType]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | Event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                clearResults();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const clearResults = () => {
        setResults([]);
        setCurrentPage(1);
        setHasMoreResults(false);
    };

    const search = (searchText: string, page = 1) => {
        if (searchController.current) {
            searchController.current.abort();
        }

        searchController.current = new AbortController();
        const {signal} = searchController.current;
        setSearchTerm(searchText);
        setCurrentPage(page);
        setIsSearching(true);

        performSearch(searchText, page, signal)
            .then((response) => {
                if (componentUnmounted.current || signal.aborted) return;
                setResults(
                    page > 1
                        ? [...results, ...response.results]
                        : response.results
                );
                setHasMoreResults(response.hasMoreResults);
            })
            .finally(() => {
                if (componentUnmounted.current) return;
                setIsSearching(false);
            });
    };

    const handleOnScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.currentTarget;
        const scrollBottom = target.scrollHeight - target.offsetHeight - 5;

        if (target.scrollTop >= scrollBottom && hasMoreResults && !isSearching) {
            search(searchTerm, currentPage + 1);
        }
    };

    return (
        <div className="typeahead" ref={dropdownRef}>
            <Search
                placeholder={placeholder}
                name={placeholder}
                value={searchTerm}
                handleChangeVal={(searchInput: any) => {
                    const inputValue = searchInput.target.value;
                    setSearchTerm(inputValue);
                    if (inputValue.length === 0) {
                        if (searchController.current) {
                            searchController.current.abort();
                        }
                        setSearchTerm('');
                        clearResults();
                    } else if (inputValue.length >= minSearchTermLength) {
                        search(inputValue);
                    }
                }}
                onClearListener={() => {
                    if (searchController.current) {
                        searchController.current.abort();
                    }
                    setSearchTerm('');
                    clearResults();
                }}
                onClick={() => {
                    if (results.length === 0 && searchTerm.length >= minSearchTermLength) {
                        search(searchTerm);
                    }
                }}
            />
            {(results.length > 0) &&
                <div aria-label="Search Results" className={"dropdown__panel"} onScroll={handleOnScroll}>
                    {resultHeader}
                    {results.map((result, index) => (
                        <div
                            className="dropdown__item"
                            key={`dropdown-item-${index}`}
                            onClick={() => {
                                onSelect && onSelect(result);
                                setSearchTerm('');
                                clearResults();
                            }}
                        >
                            {displayResult && displayResult(result)}
                        </div>
                    ))}
                    {hasMoreResults &&
                        <div className="more-results-loader">
                            <Loader
                                message="Loading more results..."
                                size="md"
                            />
                        </div>
                    }
                </div>
            }
        </div>
    );
}